<template>
  <div class="flex flex-col relative" style="height: calc(100vh - 200px)">
    <!-- WIP banner -->
    <div class="px-4 py-2 bg-yellow-50 border-b border-yellow-200 text-xs text-yellow-900">
      <FontAwesomeIcon :icon="['fas', 'triangle-exclamation']" class="mr-1.5 text-yellow-600" />
      Work in progress — UI and behavior are still being refined. Expect changes.
    </div>

    <!-- Header -->
    <div class="px-4 py-3 border-b border-gray-200 bg-white flex items-center justify-between">
      <div class="flex items-center space-x-4">
        <h2 class="text-lg font-semibold text-gray-900">
          Coding Console
          <span class="text-xs font-medium text-yellow-700 ml-1">(beta)</span>
        </h2>
        <span :class="statusPillClass">
          <span :class="statusDotClass"></span>
          {{ activeTaskId ? 'Working' : 'Paused' }}
        </span>

        <!-- Inline error so failures stay visible while a task is active.
             The idle-state center panel only shows when activeTaskId is
             null, so a grab-new failure mid-session would otherwise be
             invisible. -->
        <span
          v-if="errorMessage && activeTaskId"
          class="inline-flex items-center px-2 py-1 rounded text-xs
                 bg-red-50 text-red-700 border border-red-200"
        >
          <FontAwesomeIcon :icon="['fas', 'circle-exclamation']" class="mr-1.5" />
          {{ errorMessage }}
          <button
            @click="errorMessage = null"
            class="ml-2 text-red-600 hover:text-red-900"
            aria-label="Dismiss"
          >
            <FontAwesomeIcon :icon="['fas', 'times']" class="text-[10px]" />
          </button>
        </span>

        <!-- Control cluster -->
        <div v-if="coderContextReady" class="flex items-center space-x-2 border-l border-gray-200 pl-4">
          <button
            @click="pauseAll"
            :disabled="!activeTaskId || pausing"
            class="px-3 py-1.5 bg-amber-100 hover:bg-amber-200 disabled:bg-gray-100
                   disabled:text-gray-400 text-amber-800 rounded-md text-sm font-medium
                   flex items-center"
          >
            <FontAwesomeIcon :icon="['fas', 'pause']" class="mr-1.5 text-xs" />
            Pause
          </button>
          <!-- Grab new — blocked while a task has sat paused >24h, to force
               the coder to complete or hold it before piling on more work. -->
          <button
            @click="grabNew"
            :disabled="starting || !!stalePausedTask"
            :title="stalePausedTask ? 'Complete or put the paused task on hold before grabbing new work.' : ''"
            class="px-3 py-1.5 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-300
                   text-white rounded-md text-sm font-medium flex items-center"
          >
            <FontAwesomeIcon
              :icon="['fas', starting ? 'spinner' : 'plus']"
              :class="['mr-1.5 text-xs', starting && 'animate-spin']"
            />
            {{ starting ? 'Grabbing...' : 'Grab new' }}
          </button>
          <span
            v-if="stalePausedTask"
            class="inline-flex items-center px-2 py-1 rounded text-xs
                   bg-amber-50 text-amber-800 border border-amber-200"
          >
            <FontAwesomeIcon :icon="['fas', 'triangle-exclamation']" class="mr-1.5 text-amber-600" />
            Task paused over 24h — complete or put on hold before grabbing new.
          </span>
        </div>
      </div>

      <div class="flex items-center space-x-3">
        <!-- Coder identity / impersonation -->
        <div v-if="contextLoaded" class="flex items-center space-x-2">
          <span class="text-xs text-gray-500">Coder</span>

          <!-- Managers can switch into impersonate mode; coders are always self. -->
          <template v-if="isManager">
            <label
              class="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none"
              title="Act as another coder for review/UAT"
            >
              <input
                type="checkbox"
                v-model="impersonating"
                @change="onImpersonateToggle"
                class="rounded border-gray-300 text-primary-500 focus:ring-primary-500"
              />
              Impersonate
            </label>
            <select
              v-if="impersonating"
              v-model="selectedMemberEntityId"
              @change="onCoderChange"
              class="px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white
                     focus:outline-none focus:ring-1 focus:ring-primary-500"
            >
              <option :value="null">Select a coder...</option>
              <option
                v-for="entity in coderEntities"
                :key="entity.id"
                :value="entity.id"
              >
                {{ demoLabel(entity.name, 'practice') }} - {{ entity.type_name }}
              </option>
            </select>
            <span v-else class="text-sm font-medium text-gray-800">
              {{ selfCoderLabel }}
            </span>
          </template>

          <!-- Non-managers: always themselves, no controls. -->
          <span v-else class="text-sm font-medium text-gray-800">
            {{ userMemberEntityName || '—' }}
          </span>
        </div>
      </div>
    </div>

    <!-- Body -->
    <div class="flex-1 flex overflow-hidden">
      <!-- Sidebar -->
      <div class="w-80 shrink-0 border-r border-gray-200 bg-white flex flex-col">
        <div class="px-4 py-2 border-b border-gray-100 flex items-center justify-between">
          <span class="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            Claim Queue ({{ claimQueue.length }})
          </span>
          <!-- Where work is headed next. Coders see their own queue;
               managers previewing an impersonated coder see theirs. -->
          <button
            @click="togglePreview"
            :class="['text-[11px] font-medium px-2 py-0.5 rounded flex items-center gap-1',
                     showPreview ? 'bg-primary-100 text-primary-700' : 'text-gray-500 hover:bg-gray-100']"
            title="Preview where this coder will work next (read-only)"
          >
            <FontAwesomeIcon :icon="['fas', 'list']" class="text-[10px]" />
            Up Next
          </button>
        </div>

        <div class="flex-1 overflow-y-auto">
          <div v-if="loadingQueue" class="p-6 text-center text-sm text-gray-500">
            <FontAwesomeIcon :icon="['fas', 'spinner']" class="animate-spin mr-2" />
            Loading...
          </div>

          <div v-else-if="claimQueue.length === 0" class="p-6 text-center">
            <p class="text-sm text-gray-500">No tasks claimed.</p>
            <p class="text-xs text-gray-400 mt-1">Use Grab new above.</p>
          </div>

          <!-- Grouped by task state: active/paused on top (always shown),
               then collapsible Snoozed and On Hold sections. The actively-
               worked task pins to the top group regardless of status. -->
          <template v-else>
            <div v-for="section in queueSections" :key="section.key">
              <!-- Collapsible header for parked sections (Snoozed / On Hold) -->
              <button
                v-if="section.collapsible && section.items.length"
                @click="toggleSection(section.key)"
                class="w-full flex items-center justify-between px-4 py-2 border-t border-gray-100
                       bg-gray-50 hover:bg-gray-100 text-xs font-semibold text-gray-500
                       uppercase tracking-wide"
              >
                <span class="flex items-center gap-2">
                  <FontAwesomeIcon :icon="['fas', section.icon]" class="text-gray-400" />
                  {{ section.label }} ({{ section.items.length }})
                </span>
                <FontAwesomeIcon
                  :icon="['fas', section.open ? 'chevron-down' : 'chevron-right']"
                  class="text-[10px]"
                />
              </button>

              <ul
                v-show="!section.collapsible || section.open"
                class="divide-y divide-gray-100"
              >
                <li
                  v-for="item in section.items"
                  :key="item.task_id"
                  :class="queueItemClass(item)"
                  @click="selectTask(item)"
                >
                  <div class="flex items-start justify-between gap-2 mb-0.5">
                    <span class="sensitive text-sm font-medium text-gray-900 truncate min-w-0">
                      {{ item.patient_name || item.patient_identifier }}
                    </span>
                    <div class="flex items-center gap-1 shrink-0">
                      <span
                        v-if="item.impact_rank"
                        :class="impactPillClass(item.impact_rank)"
                        :title="`Impact Rank ${formatImpactRank(item.impact_rank)}`"
                      >
                        {{ formatImpactRank(item.impact_rank) }}
                      </span>
                      <span :class="stateBadgeClass(item)">
                        {{ stateLabel(item) }}
                      </span>
                    </div>
                  </div>
                  <div class="flex items-center justify-between gap-2 mb-1.5">
                    <span class="text-xs text-gray-700 truncate min-w-0">{{ item.task_type_name }}</span>
                    <span :class="payerTagClass(item.payer_id)">{{ payerLabel(item.payer_id) }}</span>
                  </div>
                  <div class="text-[11px] text-gray-500 leading-relaxed mb-2">
                    <div v-if="item.patient_name" class="sensitive truncate">{{ isPrivatePayer(item.payer_id) ? 'Member ID' : 'MBI' }} {{ item.patient_identifier }}</div>
                    <div v-if="item.practice_name || item.tin" class="sensitive truncate">
                      <span v-if="item.practice_name" class="sensitive" data-spoof-kind="practice">{{ item.practice_name }}</span><span v-if="item.practice_name && item.tin"> · </span><span v-if="item.tin">TIN <span class="sensitive" data-spoof-kind="identifier">{{ item.tin }}</span></span>
                    </div>
                    <div v-if="item.pcp_name" class="sensitive truncate">PCP {{ item.pcp_name }}</div>
                  </div>
                  <template v-if="stateTimeLine(item)">
                    <hr class="border-gray-100 mb-2" />
                    <div class="text-[11px] text-gray-500">{{ stateTimeLine(item) }}</div>
                  </template>
                </li>
              </ul>
            </div>
          </template>
        </div>

      </div>

      <!-- Up Next preview (read-only practice rollup). Own queue for coders;
           the impersonated coder's for managers. -->
      <div
        v-if="showPreview"
        class="w-80 shrink-0 border-r border-gray-200 bg-gray-50 flex flex-col"
      >
        <div class="px-4 py-2 border-b border-gray-200 flex items-center justify-between bg-white gap-2">
          <div class="min-w-0">
            <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide">Up Next</div>
            <div v-if="activeCoderName" class="sensitive text-sm font-medium text-gray-800 truncate">{{ activeCoderName }}</div>
          </div>
          <button
            @click="refreshPreview"
            :disabled="previewLoading"
            class="shrink-0 text-gray-500 hover:text-gray-800 disabled:text-gray-300 px-1"
            title="Refresh preview"
          >
            <FontAwesomeIcon :icon="['fas', 'arrows-rotate']" :class="['text-xs', previewLoading && 'animate-spin']" />
          </button>
        </div>
        <div class="px-4 py-1.5 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
          <span class="text-[10px] text-gray-400">Read-only · grabs next, by practice</span>
          <button @click="showPreviewHelp = !showPreviewHelp" class="text-[10px] text-primary-600 hover:underline">
            {{ showPreviewHelp ? 'Hide' : 'How it works' }}
          </button>
        </div>
        <div v-if="showPreviewHelp" class="px-4 py-2 bg-blue-50 border-b border-blue-100 text-[11px] text-gray-600 leading-relaxed">
          <p class="mb-1 font-medium text-gray-700">Grab-new picks in this order:</p>
          <ol class="list-decimal pl-4 space-y-1">
            <li><strong>Rework</strong> — QA returns &amp; coder responses jump the line first.</li>
            <li><strong>Foundations</strong> — worked ahead of PVP; you finish every chart in a Foundations practice before moving to the next.</li>
            <li><strong>PVP — current practice</strong> — stay put for up to 10 Medicare + 5 private charts before rotating, to cut EMR switches.</li>
            <li><strong>Rotate by impact rank</strong> — once the practice's batch is done, move to the highest-impact practice you haven't worked yet this round. You cycle through all your practices before returning to any one twice.</li>
          </ol>
        </div>

        <div class="flex-1 overflow-y-auto">
          <div v-if="previewLoading" class="p-6 text-center text-sm text-gray-500">
            <FontAwesomeIcon :icon="['fas', 'spinner']" class="animate-spin mr-2" />Loading...
          </div>
          <div v-else-if="previewError" class="p-6 text-center text-sm text-red-600">{{ previewError }}</div>

          <template v-else-if="preview">
            <!-- 1 · Rework (always shown — Tier 0) -->
            <div>
              <div class="flex items-center gap-2 px-4 py-2 border-t-2 border-gray-200 bg-indigo-50 text-xs font-semibold text-indigo-700 uppercase tracking-wide">
                <FontAwesomeIcon :icon="['fas', 'repeat']" class="text-indigo-400" />
                Rework ({{ previewReworkCount }})
              </div>
              <div v-if="!previewReworkCount" class="px-4 py-2 text-[11px] text-gray-400">
                None waiting — no QA returns or coder responses.
              </div>
              <ul v-else class="divide-y divide-gray-100">
                <li v-for="r in preview.rework" :key="'g' + r.task_id" class="px-4 py-2">
                  <div class="sensitive text-sm text-gray-900 break-words">{{ r.patient_name || ('Task ' + r.task_id) }}</div>
                  <div class="text-[11px] text-gray-500">{{ reworkTypeLabel(r.type) }} · ready for this coder</div>
                </li>
              </ul>
            </div>

            <!-- 2 · Current practice (only when anchored — Foundations/PVP batch) -->
            <div v-if="preview.anchor">
              <div class="flex items-center gap-2 px-4 py-2 border-t-2 border-gray-200 bg-primary-50 text-xs font-semibold text-primary-700 uppercase tracking-wide">
                <span class="w-2 h-2 rounded-full bg-primary-500"></span>
                Current practice
                <span
                  v-if="preview.anchor.program"
                  class="ml-1 px-1.5 py-0.5 rounded text-[9px] font-semibold normal-case tracking-normal"
                  :class="preview.anchor.program === 'Foundations' ? 'bg-emerald-100 text-emerald-700' : 'bg-sky-100 text-sky-700'"
                >{{ preview.anchor.program }}</span>
              </div>
              <div class="px-4 py-2">
                <div class="sensitive text-sm font-medium text-gray-900 break-words">{{ preview.anchor.practice_name }}</div>
                <div class="text-[11px] text-gray-500 mt-0.5">
                  <template v-if="preview.anchor.program === 'Foundations'">
                    Finish this practice — {{ preview.anchor.remaining }} chart{{ preview.anchor.remaining === 1 ? '' : 's' }} left (no batch limit)
                  </template>
                  <template v-else-if="preview.anchor.medicare_target != null">
                    Medicare {{ preview.anchor.medicare_worked }} of {{ preview.anchor.medicare_target }} · Private {{ preview.anchor.private_worked }} of {{ preview.anchor.private_target }} · {{ preview.anchor.remaining }} eligible here
                  </template>
                  <template v-else>
                    {{ preview.anchor.worked }} of {{ preview.anchor.target }} this batch · {{ preview.anchor.remaining }} eligible here
                  </template>
                </div>
                <div v-if="!preview.practices.length" class="text-[11px] text-gray-400 mt-1">
                  Only practice for this coder — won't rotate elsewhere.
                </div>
              </div>
            </div>

            <!-- Coverage flag (QA-spread concern) -->
            <div
              v-if="previewConcentration"
              class="px-4 py-2 bg-amber-50 border-y border-amber-100 text-[11px] text-amber-800 flex items-start gap-1.5"
            >
              <FontAwesomeIcon :icon="['fas', 'triangle-exclamation']" class="text-amber-500 shrink-0 mt-0.5" />
              <span>~{{ previewConcentration.pct }}% of eligible work is at {{ previewConcentration.name }}</span>
            </div>

            <!-- 3 · Rotation — where the coder goes next this round, then what
                 comes back next round. Two groups keep the round-robin legible. -->
            <div>
              <div class="flex items-center gap-2 px-4 py-2 border-t-2 border-gray-200 bg-gray-100 text-xs font-semibold text-gray-600 uppercase tracking-wide">
                <FontAwesomeIcon :icon="['fas', 'list']" class="text-gray-400" />
                {{ preview.anchor ? 'Up next — after this batch' : 'Up next this round' }} ({{ upcomingPractices.length }})
              </div>
              <div v-if="!preview.practices.length" class="px-4 py-3 text-sm text-gray-500">
                Nothing else eligible to grab.
              </div>
              <template v-else>
                <div class="px-4 pt-2 text-[11px] text-gray-500">
                  Rotates top → bottom, up to 10 Medicare + 5 private each.
                </div>
                <ul class="divide-y divide-gray-100">
                  <li
                    v-for="(p, i) in upcomingPractices"
                    :key="p.program + '-' + p.practice_id"
                    class="px-4 py-2 flex items-start justify-between gap-2"
                  >
                    <div class="min-w-0 flex items-start gap-2">
                      <span class="shrink-0 mt-0.5 text-[10px] font-semibold text-gray-400 tabular-nums">{{ i + 1 }}</span>
                      <div class="min-w-0">
                        <div class="sensitive text-sm text-gray-900 break-words flex items-center gap-1.5">
                          <span class="break-words sensitive" data-spoof-kind="practice">{{ p.practice_name || ('Practice ' + p.practice_id) }}</span>
                          <span
                            v-if="p.program"
                            class="shrink-0 px-1.5 py-0.5 rounded text-[9px] font-semibold sensitive"
                            :class="p.program === 'Foundations' ? 'bg-emerald-100 text-emerald-700' : 'bg-sky-100 text-sky-700'"
                          >{{ p.program }}</span>
                        </div>
                        <div class="text-[11px] text-gray-500 mt-0.5">{{ p.eligible_count }} eligible · top IR {{ formatImpactRank(p.top_impact_rank) }}</div>
                      </div>
                    </div>
                    <span
                      v-if="i === 0"
                      class="shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-primary-100 text-primary-700 font-medium"
                    >next practice</span>
                  </li>
                </ul>

                <!-- Worked earlier this round — comes back after the rotation
                     completes. Dimmed + labelled so a high-IR practice sitting
                     here reads as intentional, not a sort glitch. -->
                <div v-if="laterRoundPractices.length">
                  <div class="px-4 py-1.5 bg-gray-50 border-t border-gray-100 text-[10px] font-semibold text-gray-400 uppercase tracking-wide flex items-center gap-1.5">
                    <FontAwesomeIcon :icon="['fas', 'rotate-left']" class="text-gray-300" />
                    Comes back next round · already worked this round ({{ laterRoundPractices.length }})
                  </div>
                  <ul class="divide-y divide-gray-100 opacity-60">
                    <li
                      v-for="p in laterRoundPractices"
                      :key="'done-' + p.program + '-' + p.practice_id"
                      class="px-4 py-2"
                    >
                      <div class="sensitive text-sm text-gray-700 break-words flex items-center gap-1.5">
                        <span class="break-words sensitive" data-spoof-kind="practice">{{ p.practice_name || ('Practice ' + p.practice_id) }}</span>
                        <span
                          v-if="p.program"
                          class="shrink-0 px-1.5 py-0.5 rounded text-[9px] font-semibold sensitive"
                          :class="p.program === 'Foundations' ? 'bg-emerald-100 text-emerald-700' : 'bg-sky-100 text-sky-700'"
                        >{{ p.program }}</span>
                      </div>
                      <div class="text-[11px] text-gray-500 mt-0.5">{{ p.eligible_count }} eligible · top IR {{ formatImpactRank(p.top_impact_rank) }}</div>
                    </li>
                  </ul>
                </div>
              </template>
            </div>

            <!-- 4 · Assignments — full practice footprint, as a distinct,
                 recessed reference block (spaced + tinted + boxed) so it reads
                 as context, not another queue tier. -->
            <div v-if="preview.assignments && preview.assignments.length" class="px-3 pt-6 pb-4">
              <div class="rounded-lg border border-gray-200 bg-gray-50 overflow-hidden">
                <button
                  @click="showAssignments = !showAssignments"
                  :class="['w-full flex items-baseline gap-2 px-3 py-2 text-left bg-gray-100 hover:bg-gray-200',
                           showAssignments ? 'border-b border-gray-200' : '']"
                >
                  <FontAwesomeIcon :icon="['fas', 'layer-group']" class="text-gray-400 text-xs self-center" />
                  <span class="text-xs font-medium text-gray-600">Assignments</span>
                  <span class="text-[10px] text-gray-400">{{ preview.assignments.length }} practices · grabbable count</span>
                  <FontAwesomeIcon
                    :icon="['fas', showAssignments ? 'chevron-down' : 'chevron-right']"
                    class="text-[10px] text-gray-400 ml-auto self-center"
                  />
                </button>
                <ul v-show="showAssignments" class="divide-y divide-gray-200">
                  <li
                    v-for="a in preview.assignments"
                    :key="'asg' + a.practice_id"
                    :class="['flex items-start justify-between gap-3 px-3 py-2', a.eligible_count === 0 ? 'opacity-60' : '']"
                  >
                    <div class="min-w-0">
                      <div class="sensitive text-sm text-gray-900 break-words">{{ a.practice_name || ('Practice ' + a.practice_id) }}</div>
                      <div class="text-[11px] text-gray-500 mt-0.5">{{ (a.tiers || []).join(', ') }}</div>
                    </div>
                    <div class="shrink-0 text-right">
                      <div class="text-sm tabular-nums" :class="a.eligible_count === 0 ? 'text-gray-400' : 'text-gray-800 font-semibold'">{{ a.eligible_count }}</div>
                      <div class="text-[10px] text-gray-400 leading-none">eligible</div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Workspace -->
      <div class="flex-1 flex flex-col relative">
        <div v-if="starting" class="flex-1 flex items-center justify-center bg-gray-50">
          <div class="text-center">
            <FontAwesomeIcon :icon="['fas', 'spinner']" class="animate-spin text-primary-500 text-2xl mb-3" />
            <p class="text-sm text-gray-600">Finding your next task...</p>
          </div>
        </div>

        <div v-else-if="!activeTaskId" class="flex-1 flex items-center justify-center bg-gray-50">
          <div class="text-center max-w-md">
            <FontAwesomeIcon :icon="['fas', 'mug-hot']" class="text-gray-300 text-4xl mb-3" />
            <p class="text-sm text-gray-600 mb-1">
              {{ idleMessage }}
            </p>
            <p v-if="errorMessage" class="text-xs text-red-600 mt-3">{{ errorMessage }}</p>
          </div>
        </div>

        <Coding_Task_Workspace
          v-else
          :key="activeTaskId"
          :task-id="activeTaskId"
          :acting-as-member-entity-id="selectedMemberEntityId"
          @task-completed="handleTaskCompleted"
          class="flex-1 min-h-0"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import Coding_Task_Workspace from './panels/Coding_Task_Workspace.vue';
import { demoLabel } from '@/utils/demoSpoof';

export default {
  name: 'Coding_Console',
  components: {
    FontAwesomeIcon,
    Coding_Task_Workspace,
  },
  data() {
    return {
      claimQueue: [],
      activeTaskId: null,
      loadingQueue: false,
      starting: false,
      pausing: false,
      snoozedOpen: false,                 // collapsible queue sections,
      onHoldOpen: false,                  // folded by default to declutter
      errorMessage: null,
      currentTime: Date.now(),

      // Coder identity / impersonation context
      contextLoaded: false,
      isManager: false,
      coderEntities: [],
      userMemberEntityName: null,         // logged-in user's own coder name
      impersonating: false,               // managers only; off = act as self
      selectedMemberEntityId: null,       // impersonation target (only when impersonating)

      // "Up Next" preview (manager-only) — read-only PRACTICE-LEVEL rollup of
      // where this coder will work next (rework, anchor batch, practice pool).
      // A snapshot; the refresh button and coder-switches reload it.
      showPreview: false,
      showPreviewHelp: false,
      showAssignments: false,             // assignments block collapsed by default
      preview: null,
      previewLoading: false,
      previewError: null,

      _tickInterval: null,
    };
  },
  async mounted() {
    // Live-tick the displayed duration on the active task. 10s feels
    // responsive without flooding renders.
    this._tickInterval = setInterval(() => {
      this.currentTime = Date.now();
    }, 10_000);

    // No pagehide beacon: DRF's SessionAuthentication enforces CSRF on
    // POST and navigator.sendBeacon can't carry the CSRF token, so it
    // silently 403s. The server-side 1h reconcile-on-login is the
    // safety net for unclean exits.

    await this.loadContext();
    // Default mode is self — load the logged-in user's own queue.
    await this.refreshQueue();
  },
  beforeUnmount() {
    if (this._tickInterval) clearInterval(this._tickInterval);
  },
  computed: {
    ...mapGetters('auth', ['current_user']),
    // Self-mode label for managers. Bare "You" doesn't tell a manager whose
    // queue/attribution is active, so we append the best available identity:
    // their own coder name if they have one, else their account name, else email.
    selfCoderLabel() {
      const u = this.current_user;
      const fullName = u
        ? [u.first_name, u.last_name].filter(Boolean).join(' ').trim()
        : '';
      const identity = this.userMemberEntityName || fullName || (u && u.email);
      return identity ? `You (${identity})` : 'You';
    },
    // Ready to load/act: self mode is always ready once context loads;
    // impersonate mode needs a coder selected first.
    coderContextReady() {
      return this.contextLoaded && (!this.impersonating || !!this.selectedMemberEntityId);
    },
    idleMessage() {
      if (this.impersonating && !this.selectedMemberEntityId) {
        return 'Select a coder to impersonate.';
      }
      return this.claimQueue.length > 0
        ? 'Pick a task from the queue, or click Grab new above.'
        : 'Click Grab new above to start.';
    },
    statusPillClass() {
      const base = 'inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium';
      return this.activeTaskId
        ? `${base} bg-green-100 text-green-800`
        : `${base} bg-gray-100 text-gray-700`;
    },
    statusDotClass() {
      const base = 'w-2 h-2 rounded-full mr-2';
      return this.activeTaskId ? `${base} bg-green-500` : `${base} bg-gray-400`;
    },
    // A task left paused (status 1, interval closed) for more than 24h.
    // Reads this.currentTime (10s tick) so the block engages live without a
    // reload. Snoozed/on-hold are intentional parking — they don't count.
    stalePausedTask() {
      const cutoff = this.currentTime - 24 * 60 * 60 * 1000;
      return this.claimQueue.find(item =>
        item.status_id === 1
        && !item.is_active
        && item.latest_stopped_at
        && new Date(item.latest_stopped_at).getTime() < cutoff
      ) || null;
    },
    // Partition the (impact-rank-sorted) queue into display sections.
    // Active/paused stay on top and always visible; snoozed and on-hold
    // tasks drop into their own collapsible groups. The actively-worked
    // task pins to the top group regardless of status — a coder can resume
    // a snoozed/on-hold task, opening an interval without changing status.
    queueSections() {
      const active = [];
      const snoozed = [];
      const onHold = [];
      for (const item of this.claimQueue) {
        if (item.is_active) active.push(item);
        else if (item.status_id === 4) snoozed.push(item);
        else if (item.status_id === 7) onHold.push(item);
        else active.push(item);          // status 1 (paused) + any fallback
      }
      return [
        { key: 'active', collapsible: false, open: true, items: active },
        { key: 'snoozed', label: 'Snoozed', icon: 'clock', collapsible: true, open: this.snoozedOpen, items: snoozed },
        { key: 'onhold', label: 'On Hold', icon: 'hand', collapsible: true, open: this.onHoldOpen, items: onHold },
      ];
    },
    // Display name of the coder the preview is for (impersonated or self).
    activeCoderName() {
      if (this.impersonating && this.selectedMemberEntityId) {
        const e = this.coderEntities.find(c => c.id === this.selectedMemberEntityId);
        return e ? e.name : null;
      }
      return this.userMemberEntityName || null;
    },
    // Rework count for the preview section badge (grabbable-only — what this
    // coder would actually grab; rework held for another coder isn't shown).
    previewReworkCount() {
      if (!this.preview) return 0;
      return this.preview.rework.length;
    },
    // Coverage flag: when one practice holds most of the eligible pool, a
    // reviewer will camp there (the 2LR QA-spread concern). Flags ≥60% of a
    // pool of ≥15, counting the anchor's remaining + every rollup practice.
    previewConcentration() {
      const p = this.preview;
      if (!p) return null;
      const buckets = [];
      if (p.anchor) buckets.push({ name: p.anchor.practice_name, n: p.anchor.remaining || 0 });
      for (const pr of p.practices) buckets.push({ name: pr.practice_name, n: pr.eligible_count });
      const total = buckets.reduce((s, b) => s + b.n, 0);
      if (total < 15) return null;
      const top = buckets.reduce((a, b) => (b.n > a.n ? b : a), { n: 0, name: null });
      const pct = Math.round((top.n / total) * 100);
      return pct >= 60 ? { name: top.name, pct } : null;
    },
    // Rotation grouping for the "next practices" list. The backend returns the
    // practices already in rotation order; we split them so the coder can see
    // where the rotation is heading vs. what they've already worked this round.
    upcomingPractices() {
      if (!this.preview) return [];
      const up = this.preview.practices.filter((p) => !p.worked_this_cycle);
      if (up.length) return up;
      // Edge: a new round is imminent — every remaining practice was already
      // worked this round. The backend still sorts the picker's actual next
      // practice first, so surface just that one here rather than an empty list.
      return this.preview.practices.slice(0, 1);
    },
    // Practices worked earlier in the current round — demoted to the bottom and
    // shown dimmed under their own heading, so a high-IR practice sitting low is
    // clearly "already done this round, comes back next round," not mis-sorted.
    laterRoundPractices() {
      if (!this.preview) return [];
      const up = this.preview.practices.filter((p) => !p.worked_this_cycle);
      if (up.length) return this.preview.practices.filter((p) => p.worked_this_cycle);
      return this.preview.practices.slice(1);  // first is surfaced as "next" above
    },
  },
  methods: {
    demoLabel,
    async loadContext() {
      try {
        const { data } = await this.$axios.get('/api/coding/console-context/');
        this.isManager = !!data.is_manager;
        this.coderEntities = data.coder_entities || [];
        this.userMemberEntityName = data.user_member_entity_name;
        // selectedMemberEntityId stays null in self mode — it's set only
        // when a manager turns on Impersonate and picks a coder.
      } catch (err) {
        console.error('Failed to load console context:', err);
        this.errorMessage = 'Failed to load console context';
      } finally {
        this.contextLoaded = true;
      }
    },

    // Manager toggled Impersonate on/off. Off → act as self (no acting_as);
    // on → wait for a coder to be picked before loading anything.
    async onImpersonateToggle() {
      this.selectedMemberEntityId = null;
      this.activeTaskId = null;
      this.claimQueue = [];
      this.preview = null;
      this.previewError = null;
      this.errorMessage = null;
      if (!this.impersonating) {
        await this.refreshQueue();
        if (this.showPreview) await this.refreshPreview();
      }
    },

    async onCoderChange() {
      this.claimQueue = [];
      this.preview = null;
      this.previewError = null;
      this.activeTaskId = null;
      if (this.selectedMemberEntityId) {
        await this.refreshQueue();
        if (this.showPreview) await this.refreshPreview();
      }
    },

    // acting_as is sent ONLY for genuine impersonation. In self mode we send
    // nothing, so the backend resolves the logged-in user's own entities.
    actingAsParam() {
      return this.impersonating && this.selectedMemberEntityId
        ? { acting_as_member_entity_id: this.selectedMemberEntityId }
        : {};
    },

    async refreshQueue() {
      if (!this.coderContextReady) return;
      this.loadingQueue = true;
      this.errorMessage = null;
      try {
        const response = await this.$axios.get('/api/coding/claim-queue/', {
          params: this.actingAsParam(),
        });
        this.claimQueue = response.data.claim_queue || [];
        const active = this.claimQueue.find(t => t.is_active);
        this.activeTaskId = active ? active.task_id : null;
      } catch (err) {
        console.error('Failed to load claim queue:', err);
        this.errorMessage = 'Failed to load claim queue';
      } finally {
        this.loadingQueue = false;
      }
    },

    // "Up Next" preview. Opening loads it once; refresh reloads.
    async togglePreview() {
      this.showPreview = !this.showPreview;
      if (this.showPreview && !this.preview && !this.previewError) {
        await this.refreshPreview();
      }
    },

    // Read-only practice-level rollup of where the coder will work next
    // (Tier 0/1/2, same logic as the live picker). Coders see their own;
    // impersonation (acting_as) stays manager-only server-side.
    async refreshPreview() {
      if (!this.coderContextReady) return;
      this.previewLoading = true;
      this.previewError = null;
      try {
        const { data } = await this.$axios.get('/api/coding/queue-preview/', {
          params: this.actingAsParam(),
        });
        this.preview = data;
      } catch (err) {
        console.error('Failed to load queue preview:', err);
        this.previewError = err.response?.data?.error || 'Failed to load queue preview';
        this.preview = null;
      } finally {
        this.previewLoading = false;
      }
    },

    reworkTypeLabel(t) {
      if (t === 'qa_returned') return 'QA return';
      if (t === 'coder_response') return 'Coder response';
      return 'Rework';
    },

    applyStartResponse(data) {
      const queue = data?.claim_queue;
      if (Array.isArray(queue)) {
        this.claimQueue = queue;
        const active = queue.find(t => t.is_active);
        this.activeTaskId = active ? active.task_id : null;
      }
    },

    async selectTask(item) {
      if (item.is_active && item.task_id === this.activeTaskId) return;
      this.starting = true;
      this.errorMessage = null;
      try {
        const { data } = await this.$axios.post('/api/coding/start/', {
          task_id: item.task_id,
          ...this.actingAsParam(),
        });
        this.applyStartResponse(data);
      } catch (err) {
        console.error('Failed to start task:', err);
        this.errorMessage = err.response?.data?.error || 'Failed to start task';
        await this.refreshQueue();
      } finally {
        this.starting = false;
      }
    },

    async grabNew() {
      if (this.stalePausedTask) {
        this.errorMessage = 'Complete or put the paused task on hold before grabbing new.';
        return;
      }
      if (this.impersonating && !this.selectedMemberEntityId) {
        this.errorMessage = 'Select a coder to impersonate first.';
        return;
      }
      this.starting = true;
      this.errorMessage = null;
      try {
        const { data } = await this.$axios.post('/api/coding/start/', {
          ...this.actingAsParam(),
        });
        this.applyStartResponse(data);
      } catch (err) {
        console.error('Failed to grab new task:', err);
        this.errorMessage = err.response?.data?.error || 'No work available right now.';
        await this.refreshQueue();
      } finally {
        this.starting = false;
      }
    },

    async pauseAll() {
      this.pausing = true;
      // Optimistic flip — pill goes Paused immediately so the user sees
      // their click landed. The /pause endpoint is idempotent, so the
      // server-side close just makes the UI match reality.
      const priorActiveTaskId = this.activeTaskId;
      this.activeTaskId = null;
      try {
        await this.$axios.post('/api/coding/pause/', this.actingAsParam());
        await this.refreshQueue();
      } catch (err) {
        console.error('Pause failed:', err);
        this.activeTaskId = priorActiveTaskId;  // roll back optimism
        this.errorMessage = 'Pause failed — try again.';
      } finally {
        this.pausing = false;
      }
    },

    handleTaskCompleted(payload) {
      const next = payload?.response?.claim_queue;
      if (Array.isArray(next)) {
        this.claimQueue = next;
        const active = next.find(t => t.is_active);
        this.activeTaskId = active ? active.task_id : null;
      } else {
        this.refreshQueue();
      }
    },

    queueItemClass(item) {
      const base = 'p-3 cursor-pointer hover:bg-gray-50 transition-colors';
      return item.task_id === this.activeTaskId
        ? `${base} bg-primary-50 border-l-4 border-primary-500`
        : base;
    },

    toggleSection(key) {
      if (key === 'snoozed') this.snoozedOpen = !this.snoozedOpen;
      else if (key === 'onhold') this.onHoldOpen = !this.onHoldOpen;
    },

    // State badge reflects the task's true state, not just open-interval.
    // is_active wins (the coder is on it now); otherwise status drives it.
    stateLabel(item) {
      if (item.is_active) return 'Working';
      if (item.status_id === 4) return 'Snoozed';
      if (item.status_id === 7) return 'On Hold';
      return 'Paused';
    },

    stateBadgeClass(item) {
      const base = 'inline-flex px-1.5 py-0.5 text-[10px] font-medium rounded';
      if (item.is_active) return `${base} bg-green-100 text-green-800`;
      if (item.status_id === 4) return `${base} bg-amber-100 text-amber-800`;
      if (item.status_id === 7) return `${base} bg-indigo-100 text-indigo-700`;
      return `${base} bg-gray-100 text-gray-600`;
    },

    formatImpactRank(ir) {
      if (ir == null) return '';
      const n = Number(ir);
      if (Number.isNaN(n)) return String(ir);
      return n.toFixed(1);
    },

    isPrivatePayer(payerId) {
      return !!payerId && payerId !== 'CMS';
    },

    payerLabel(payerId) {
      return this.isPrivatePayer(payerId) ? 'Private' : 'Medicare';
    },

    payerTagClass(payerId) {
      const base = 'shrink-0 inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-medium leading-none';
      return this.isPrivatePayer(payerId)
        ? `${base} border-violet-200 text-violet-600`
        : `${base} border-gray-200 text-gray-500`;
    },

    impactPillClass(ir) {
      const base = 'inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold leading-none tabular-nums';
      const n = Number(ir) || 0;
      // Buckets calibrated to the observed IR distribution
      // (formula max ~18, real top tail ~8–13).
      if (n >= 8) return `${base} bg-orange-100 text-orange-700`;
      if (n >= 4) return `${base} bg-amber-50 text-amber-700`;
      if (n >= 2) return `${base} bg-blue-50 text-blue-700`;
      return `${base} bg-gray-100 text-gray-600`;
    },

    // Per-card status line. One event time + one cumulative duration, so a
    // reader never has two timestamps to (wrongly) subtract.
    //   Working  → Started/Resumed {when} · {total time on task}
    //   Paused   → Paused {when} · {total time on task}
    //   Snoozed  → Snoozed until {wake date}
    //   On Hold  → '' (the section header already conveys the state)
    stateTimeLine(item) {
      if (item.is_active) {
        // "Resumed" once there are prior closed intervals; else first session.
        const verb = item.closed_intervals_seconds > 0 ? 'Resumed' : 'Started';
        const dur = this.formatDuration(this.displayedSeconds(item));
        return `${verb} ${this.formatSmartDateTime(item.latest_started_at)} · ${dur}`;
      }
      if (item.status_id === 4) {
        return item.snooze_until
          ? `Snoozed until ${this.formatSmartDateTime(item.snooze_until)}`
          : 'Snoozed';
      }
      if (item.status_id === 7) return '';
      if (!item.latest_stopped_at) return '';
      const dur = this.formatDuration(this.displayedSeconds(item));
      return `Paused ${this.formatSmartDateTime(item.latest_stopped_at)} · ${dur}`;
    },

    // Smart local datetime: time-only when it's today, else "Jun 11, 12:02 PM".
    // No timeZone option → renders in the viewer's local zone (browser/OS).
    formatSmartDateTime(iso) {
      if (!iso) return '';
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) return '';
      const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      const now = new Date();
      const sameDay = date.getFullYear() === now.getFullYear()
        && date.getMonth() === now.getMonth()
        && date.getDate() === now.getDate();
      if (sameDay) return time;
      const day = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      return `${day}, ${time}`;
    },

    displayedSeconds(item) {
      const closed = item.closed_intervals_seconds || 0;
      if (!item.is_active || !item.latest_started_at) return closed;
      const startedMs = new Date(item.latest_started_at).getTime();
      const liveSeconds = Math.max(0, Math.floor((this.currentTime - startedMs) / 1000));
      return closed + liveSeconds;
    },

    formatDuration(totalSeconds) {
      if (totalSeconds < 60) return `${totalSeconds}s`;
      const minutes = Math.floor(totalSeconds / 60);
      if (minutes < 60) return `${minutes}m`;
      const hours = Math.floor(minutes / 60);
      const remMin = minutes % 60;
      return `${hours}h ${remMin}m`;
    },
  },
};
</script>
